import { useDeleteFileMutation } from "apollo/hooks/mutations";
import { composeProgress } from "apollo/utils";
import {
  CLINICAL_CONSULTATION_ACTION,
  DOCS_FORM_NAMES,
  FILE_DELETE_QUERY_WITHOUT_FORM_SIDE_EFFECTS,
  FILE_SCOPE,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  RECARE_FILE_TYPE,
  RECARE_FILE_TYPE_TO_CATEGORY,
  SEARCH_ACTION_EDIT_SEARCH,
  SEARCH_ACTION_GENERATE_FORM
} from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { getErrorMessage } from "core/model/utils/errors";
import { activateDocsSaveMetadata } from "core/model/utils/featureFlags";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  EncryptedField,
  FileParams,
  GKVGeneralFormData,
  Patient,
  QueryProgress,
} from "core/types";
import {
  FileUploadStates,
  useUploadFile,
} from "dsl/ecosystems/PatientFiles/useUploadFile";
import { useClinicalConsultationAction } from "dsl/hooks";
import useSearchAction from "dsl/hooks/useSearchAction";
import cloneDeep from "lodash/cloneDeep";
import { useCallback, useState } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { useConnecterWithLoggedAccount } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { PdfConfig } from "./Forms";
import { useCreateFormPdf } from "./Forms/DischargeLetters/useCreateFormPdf";
import { SourceInformation } from "./useDocsForm";
import { buildSaveFormMetadata, FORM_NAME_TO_FILE_TYPE } from "./utils";

export function useDocsFormOnSubmit<T extends DocsFormName>({
  auction,
  auctionForms,
  consultation,
  docsFillFormResult,
  formName,
  patient,
  pdfConfig,
  previousFormData,
  sourceInformation,
}: {
  auction: Auction | undefined;
  auctionForms: AuctionForm[];
  consultation: ClinicalConsultation | undefined;
  docsFillFormResult: DocsFormStatusResponse | null;
  formName: T;
  patient: Patient;
  pdfConfig: PdfConfig<T>;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  sourceInformation: SourceInformation<T>;
}) {
  const translations = useTranslations();
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [uploadStates, setUploadStates] = useState<FileUploadStates>({});

  const fileParams: FileParams = auction
    ? `auction_id=${auction.id}&scope=${FILE_SCOPE.PATIENT_FILES}`
    : consultation
      ? `clinical_consultation_id=${consultation.id}&scope=${FILE_SCOPE.PATIENT_FILES}`
      : `patient_id=${patient.id}&scope=${FILE_SCOPE.PATIENT_FILES}`;

  const createFormPdf = useCreateFormPdf({
    auctionForms,
    externalId: patient.external_id ?? "",
    formName,
  });

  const { handleUploadFile } = useUploadFile({
    action: undefined,
    auction,
    params: fileParams,
    setUploadStates,
    uploadStates,
  });

  const [deleteUploadedFile] = useDeleteFileMutation({});

  const [
    generateAuctionForm,
    generateAuctionFormProgress,
    resetProgressAuctionForm,
  ] = useSearchAction({ actionType: SEARCH_ACTION_GENERATE_FORM });

  const [
    generateFormConsultation,
    generateFormConsultationProgress,
    resetProgressConsultation,
  ] = useClinicalConsultationAction({
    actionType: CLINICAL_CONSULTATION_ACTION.GENERATE_FORM,
  });

  const [editSearch] = useSearchAction({
    actionType: SEARCH_ACTION_EDIT_SEARCH,
  });

  const account = useConnecterWithLoggedAccount();

  const onSubmit = useCallback(
    async (
      formData: DocsFormDataMapped<T>,
      isDirty: boolean,
    ): Promise<boolean> => {
      if (!isDirty) return true;

      setProgress(QUERY_PROGRESS_PENDING);

      let uploadedFileId: number | undefined;

      const cleanupUploadedFile = async () => {
        if (uploadedFileId === undefined) return;
        const id = uploadedFileId;
        uploadedFileId = undefined;
        try {
          await deleteUploadedFile(undefined, {
            id,
            params:
              `${fileParams}&${FILE_DELETE_QUERY_WITHOUT_FORM_SIDE_EFFECTS}` as FileParams,
          });
        } catch (err) {
          console.error(
            "[useDocsFormOnSubmit] Failed to delete orphaned PDF after save failure:",
            getErrorMessage(err),
          );
        }
      };

      const fail = async () => {
        await cleanupUploadedFile();
        setProgress(QUERY_PROGRESS_FAILED);
        return false;
      };

      try {
        if (!consultation && !auction) {
          console.error("auction is not defined");
          return await fail();
        }

        const pdfBytes = await createFormPdf<T>({
          documentNumber: -1,
          formData,
          pdfConfig,
        });

        if (!pdfBytes) {
          console.error("could not create pdf on form save");
          return await fail();
        }

        const fileType = FORM_NAME_TO_FILE_TYPE[formName];
        const fileCategory = RECARE_FILE_TYPE_TO_CATEGORY[fileType];
        const pdfFile = new File([pdfBytes], `${formName}.pdf`, {
          type: "application/pdf",
        });
        const base64Content = btoa(
          Array.from(pdfBytes, (byte) =>
            String.fromCharCode(byte as number),
          ).join(""),
        );
        const fileName = pdfConfig.downloadPdfFileName(translations);

        try {
          const uploadedFile = await handleUploadFile({
            category: fileCategory,
            content: base64Content,
            file: pdfFile,
            file_scope: FILE_SCOPE.PATIENT_FILES,
            fileName: `${fileName}.pdf`,
            form_synced_with_file: true,
            kis_document_id_string: undefined,
            recare_file_type: fileType,
          });
          if (!uploadedFile) {
            return await fail();
          }
          uploadedFileId = uploadedFile.id;
        } catch (err) {
          console.error("error uploading pdf on form save", err);
          uploadedFileId = undefined;
          return await fail();
        }

        const isGkv =
          fileType === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
          fileType === RECARE_FILE_TYPE.GKV_MEDICAL_FORM;

        const metadata = buildSaveFormMetadata({
          auctionForms,
          docsFillFormResult,
          formData,
          formName,
          includeAuctionSchemaVersion: !!auction,
          previousFormData,
          sourceInformation,
        });

        // Discharge letters are saved with a { values: {...} } wrapper; all others submit raw form data
        const isMigratedForm =
          formName === DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT ||
          formName === DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY;
        const submittedData = isMigratedForm ? { values: formData } : formData;

        const syncValidatedFormToAiCore = async () => {
          const careseekerId = patient.careseeker?.id;
          if (!careseekerId || !activateDocsSaveMetadata) {
            return;
          }
          try {
            await aiApi.docs.saveValidatedForm({
              auctionId: auction?.id,
              careseekerId,
              consultationId: consultation?.id,
              formName,
              patientId: patient.id,
              validatedForm: { form_data: formData, metadata },
            });
          } catch (err) {
            console.error(
              "[useDocsFormOnSubmit] Failed to sync validated form to ai-core:",
              getErrorMessage(err),
            );
          }
        };

        if (consultation) {
          const consultationResult = await generateFormConsultation({
            consultation,
            context: {
              clinical_consultation: {
                ...consultation,
                forms: [
                  {
                    form_data: convertEncryptedFieldOut(
                      JSON.stringify(submittedData),
                    ) as EncryptedField,
                    form_type: fileType,
                    metadata,
                  },
                ],
              },
            },
            onCompleted: () => resetProgressConsultation(),
            patient,
          });
          if (!consultationResult) {
            return await fail();
          }
          await syncValidatedFormToAiCore();
          setProgress(QUERY_PROGRESS_NOT_STARTED);
          return true;
        }

        if (!auction) {
          console.error("auction is not defined");
          return await fail();
        }

        // GKV general form: sync case_id to patient external_id if changed
        if (isGkv) {
          const caseId = (formData as GKVGeneralFormData).case_id;
          if (caseId && auction.patient.external_id !== caseId) {
            const auctionWithUpdatedPatient = cloneDeep(auction);
            auctionWithUpdatedPatient.patient.external_id = caseId;
            const editedAuction = await editSearch({
              auction,
              context: { auction: auctionWithUpdatedPatient },
            });
            if (editedAuction === null) {
              return await fail();
            }
          }
        }

        const updatedAuction = {
          ...auction,
          forms: [
            {
              form_data: convertEncryptedFieldOut(
                JSON.stringify(submittedData),
              ),
              form_type: fileType,
              metadata,
            },
          ],
        } as Auction;

        const savedAuction = await generateAuctionForm({
          auction,
          context: {
            auction: updatedAuction,
            form_type: fileType,
          },
          onCompleted: () => resetProgressAuctionForm(),
          onError: (err) => {
            console.error(`error saving form - ${getErrorMessage(err)}`);
          },
        });

        if (!savedAuction) {
          return await fail();
        }

        await syncValidatedFormToAiCore();
        setProgress(QUERY_PROGRESS_NOT_STARTED);
        return true;
      } catch (err) {
        console.error("error creating pdf on form save", err);
        return await fail();
      }
    },
    [
      account,
      auction,
      auctionForms,
      consultation,
      createFormPdf,
      deleteUploadedFile,
      docsFillFormResult,
      editSearch,
      fileParams,
      formName,
      generateAuctionForm,
      generateFormConsultation,
      handleUploadFile,
      patient,
      pdfConfig,
      previousFormData,
      resetProgressAuctionForm,
      resetProgressConsultation,
      sourceInformation,
      translations,
    ],
  );

  return {
    onSubmit,
    onSubmitProgress: composeProgress([
      progress,
      generateAuctionFormProgress,
      generateFormConsultationProgress,
    ]),
  };
}
